/**
 * SEO Helpers — Shared utilities for content SEO pages.
 * Used by Service, City, and Blog page templates.
 */

const BASE = "https://www.invitationwala.online";

export const SITE_BASE = BASE;

// Build a canonical URL from a path
export const canonicalFor = (path = "/") =>
  `${BASE}${path.startsWith("/") ? path : `/${path}`}`;

// Build breadcrumb items for any page
export const buildBreadcrumbs = (segments) => {
  // segments: [{ label, href }]
  return [{ label: "Home", href: "/" }, ...segments];
};

// JSON-LD BreadcrumbList schema
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: canonicalFor(it.href),
  })),
});

// JSON-LD FAQPage schema
export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// JSON-LD Article schema (for blog posts)
export const articleSchema = ({
  title,
  description,
  slug,
  image,
  datePublished,
  dateModified,
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  image: [image],
  datePublished,
  dateModified: dateModified || datePublished,
  author: {
    "@type": "Organization",
    name: "InvitationWala",
    url: BASE,
  },
  publisher: {
    "@type": "Organization",
    name: "InvitationWala",
    logo: {
      "@type": "ImageObject",
      url: `${BASE}/logos/invitation-wala.png`,
    },
  },
  mainEntityOfPage: canonicalFor(`/blog/${slug}`),
});

// JSON-LD Service schema for service pages
export const servicePageSchema = ({
  serviceName,
  description,
  slug,
  image,
  areaServed,
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: serviceName,
  description,
  serviceType: serviceName,
  image,
  provider: {
    "@type": "Organization",
    name: "InvitationWala",
    url: BASE,
  },
  areaServed: areaServed || [
    { "@type": "Country", "name": "India" },
    { "@type": "Place", "name": "NRI Worldwide" },
  ],
  url: canonicalFor(`/services/${slug}`),
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "INR",
    lowPrice: "999",
    highPrice: "25000",
    offerCount: "20",
  },
});

// JSON-LD LocalBusiness schema for city pages
export const localBusinessForCity = ({ city, country, slug }) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: `InvitationWala — ${city}`,
  description: `Premium digital wedding invitation design studio serving ${city}. Cinematic invite films, save-the-dates and luxury stationery delivered on WhatsApp.`,
  url: canonicalFor(`/locations/${slug}`),
  image:
    "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/wssipa5q_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg",
  telephone: "+91-7380587328",
  priceRange: "₹₹₹",
  areaServed: { "@type": "City", name: city },
  address: {
    "@type": "PostalAddress",
    addressLocality: city,
    addressCountry: country,
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "287",
  },
});
